import './WithdrawalHistory.css';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWithdrawalHistory } from './services/paymentService';
import { useWallet } from './WalletContext';

const ITEMS_PER_PAGE = 15;

function buildPageNumbers(current, total) {
  if (total <= 1) return [];
  const pages = [];
  const rangeStart = Math.max(2, current - 1);
  const rangeEnd = Math.min(total - 1, current + 1);
  pages.push(1);
  if (rangeStart > 2) pages.push('…');
  for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);
  if (rangeEnd < total - 1) pages.push('…');
  if (total > 1) pages.push(total);
  return pages;
}

const getStatus = (w) => {
  if (w.withdrawal_completed_at) return 'completed';
  if (w.withdrawal_request === '0') return 'rejected';
  return 'pending';
};

const FILTERS = [
  { key: 'all',       label: 'All' },
  { key: 'pending',   label: 'Pending' },
  { key: 'completed', label: 'Completed' },
  { key: 'rejected',  label: 'Rejected' },
];

function WithdrawalHistory({ onBack }) {
  const navigate = useNavigate();
  const { walletBalance } = useWallet();
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const listRef = useRef(null);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        const user = JSON.parse(localStorage.getItem('user'));
        if (!user || !user.id) { setError('User not found. Please login again.'); return; }
        const response = await fetchWithdrawalHistory(user.id);
        if (response.success && Array.isArray(response.withdrawals)) {
          const sorted = [...response.withdrawals].sort(
            (a, b) => new Date(b.withdrawal_requested_at) - new Date(a.withdrawal_requested_at)
          );
          setWithdrawals(sorted);
        } else {
          setWithdrawals([]);
        }
      } catch (err) {
        console.error('Error fetching withdrawal history:', err);
        setError('Failed to load withdrawal history. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, []);

  // counts per status
  const counts = {
    all:       withdrawals.length,
    pending:   withdrawals.filter(w => getStatus(w) === 'pending').length,
    completed: withdrawals.filter(w => getStatus(w) === 'completed').length,
    rejected:  withdrawals.filter(w => getStatus(w) === 'rejected').length,
  };

  const filtered = activeFilter === 'all'
    ? withdrawals
    : withdrawals.filter(w => getStatus(w) === activeFilter);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const pageItems = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  const pageNumbers = buildPageNumbers(currentPage, totalPages);

  const handleFilter = (key) => {
    setActiveFilter(key);
    setCurrentPage(1);
    listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToPage = (page) => {
    setCurrentPage(page);
    listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  };

  const badgeClass = (w) => {
    const s = getStatus(w);
    return s === 'completed' ? 'wh-badge-completed' : s === 'rejected' ? 'wh-badge-rejected' : 'wh-badge-pending';
  };
  const badgeText = (w) => getStatus(w).toUpperCase();

  return (
    <div className="wh-page" ref={listRef}>
      {/* Header */}
      <div className="wh-header">
        <button className="wh-back-btn" onClick={onBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <h3 className="wh-title">Withdrawal History</h3>
        <button className="wallet-btn" onClick={() => navigate("/funds")}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="6" width="20" height="14" rx="2" stroke="white" strokeWidth="2"/>
            <path d="M2 10H22" stroke="white" strokeWidth="2"/>
            <circle cx="18" cy="15" r="1.5" fill="white"/>
          </svg>
          <span className="wallet-balance">₹ {walletBalance}</span>
        </button>
      </div>

      {loading ? (
        <div className="wh-center">
          <div className="wh-spinner" />
          <p className="wh-hint">Loading...</p>
        </div>
      ) : error ? (
        <div className="wh-center">
          <p className="wh-error-text">{error}</p>
        </div>
      ) : withdrawals.length === 0 ? (
        <div className="wh-center">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#cbd5e0" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4m0 4h.01" strokeLinecap="round" />
          </svg>
          <p className="wh-hint">No withdrawal history found</p>
        </div>
      ) : (
        <>
          {/* Summary count cards */}
          <div className="wh-summary">
            <div className="wh-sum-card wh-sum-all">
              <span className="wh-sum-count">{counts.all}</span>
              <span className="wh-sum-label">Total</span>
            </div>
            <div className="wh-sum-card wh-sum-pending">
              <span className="wh-sum-count">{counts.pending}</span>
              <span className="wh-sum-label">Pending</span>
            </div>
            <div className="wh-sum-card wh-sum-completed">
              <span className="wh-sum-count">{counts.completed}</span>
              <span className="wh-sum-label">Completed</span>
            </div>
            <div className="wh-sum-card wh-sum-rejected">
              <span className="wh-sum-count">{counts.rejected}</span>
              <span className="wh-sum-label">Rejected</span>
            </div>
          </div>

          {/* Filter tabs */}
          <div className="wh-tabs">
            {FILTERS.map(({ key, label }) => (
              <button
                key={key}
                className={`wh-tab${activeFilter === key ? ' wh-tab-active' : ''} wh-tab-${key}`}
                onClick={() => handleFilter(key)}
              >
                {label}
                <span className="wh-tab-count">{counts[key]}</span>
              </button>
            ))}
          </div>

          {/* List */}
          {filtered.length === 0 ? (
            <div className="wh-center">
              <p className="wh-hint">No {activeFilter} withdrawals</p>
            </div>
          ) : (
            <>
              <div className="wh-list">
                {pageItems.map((w) => (
                  <div key={w.id} className="wh-card">
                    <div className="wh-icon-wrap">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 19V5M5 12l7-7 7 7" />
                      </svg>
                    </div>

                    <div className="wh-mid">
                      <span className="wh-label">Withdraw Funds</span>
                      <span className="wh-date">{formatDate(w.withdrawal_requested_at)}</span>
                      <span className="wh-order">
                        {w.id ? `#WDL_${String(w.id).padStart(10, '0')}` : '#—'}
                      </span>
                    </div>

                    <div className="wh-right">
                      <span className="wh-amount">
                        -{'₹'}{parseFloat(w.withdrawal_amount) % 1 === 0
                          ? parseFloat(w.withdrawal_amount)
                          : parseFloat(w.withdrawal_amount).toFixed(2)}
                      </span>
                      <span className={`wh-badge ${badgeClass(w)}`}>{badgeText(w)}</span>
                    </div>
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="wh-pagination">
                  <button className="wh-pg-btn wh-pg-nav" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M15 18L9 12L15 6" />
                    </svg>
                  </button>

                  {pageNumbers.map((pg, idx) =>
                    pg === '…' ? (
                      <span key={`e-${idx}`} className="wh-pg-ellipsis">…</span>
                    ) : (
                      <button key={pg} className={`wh-pg-btn${currentPage === pg ? ' wh-pg-active' : ''}`} onClick={() => goToPage(pg)}>
                        {pg}
                      </button>
                    )
                  )}

                  <button className="wh-pg-btn wh-pg-nav" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 18L15 12L9 6" />
                    </svg>
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

export default WithdrawalHistory;
